import { Container, Graphics, Sprite, Text, TilingSprite, type Texture } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import { AudioBus } from '../../audio/AudioBus';
import { DefaultRandom, type Random } from '../../core/Random';
import { Easings, TweenGroup } from '../../core/Tween';

const FONT_FAMILY = '"Fredoka", system-ui, -apple-system, sans-serif';
// 2026-06-04 — bumped 360 → 440 so the bigger commissioned card faces
// (now ~190px tall) sit inside the panel with breathing room. Width
// up too so the two cards have shoulders.
// Wider + taller (producer brief 2026-06-04: "the panel feels compact on
// desktop, want it to feel more dramatic" + "need more space below the
// coins"). Width 460 → 580 fills more of the slot frame on wider
// viewports. Height 380 → 460 opens ~75px of breathing space between
// the amount row and the card tops so the eye can focus on each line.
const PANEL_HEIGHT = 460;
const PANEL_MAX_WIDTH = 580;
// Coin icon next to the amount number so the player reads "30" as
// "30 coins" without ambiguity. 32px square matches the amount text's
// cap height; the painted disc reads even when amountText scales
// 1.35× for the WIN celebration.
const COIN_SIZE = 32;
// Pick-stage card size — used by {@link buildColorButton} for the two
// tappable red / black cards before the player chooses.
const CARD_HEIGHT = 190;
// Reveal card size — used by {@link paintCardFace} / {@link paintCardBack}
// after the player picks and the card flips. Bigger than the pick stage
// so the moment of reveal has more weight (producer brief 2026-06-04:
// "make the winning card larger"). Card-back dimensions in
// paintCardBack scale off this so the flip stays coherent — back +
// front are the same silhouette through the animation.
const REVEAL_CARD_HEIGHT = 240;
// Title ribbon height. The commissioned PNG straddles the panel top
// edge — anchored vertically centred on {@code panelY} — so half the
// banner extends above the panel, like a chapter header.
const TITLE_HEIGHT = 130;

export type GambleColor = 'red' | 'black';

export interface GambleResult {
  outcome: 'win' | 'lose';
  picked: GambleColor;
  actualColor: GambleColor;
  /** Winnings after the gamble: 2× on win, 0 on lose. */
  winningsAfter: number;
}

/** Decides a gamble somewhere else (the server) once the player has picked.
 *  Rejects when there's no answer; the overlay then closes with nothing
 *  changed. */
export type GambleDecide = (
  picked: GambleColor,
) => Promise<{ actualColor: GambleColor; winningsAfter: number }>;

/**
 * Double-or-nothing gamble overlay. Opens after a winning spin: two colour
 * buttons (RED / BLACK), player picks one, a card flips to reveal its colour.
 * Match = win (winnings × 2). Mismatch = lose (winnings = 0).
 *
 * <p>v0 is fair 50/50 — the actual colour is decided by
 * {@link Random#nextBoolean} at pick time, independent of the player's
 * choice. cs-core's gamble had the same shape; the riskier "guess two cards
 * in a row" variant lands with the Phase B Logic refactor.
 *
 * <p><strong>Layout:</strong> all child display objects (buttons + card) are
 * created up front in the constructor and positioned by {@link layout}
 * regardless of visibility. {@link play} only flips visible state — there is
 * no separate "build at play time" path, which previously left the buttons
 * at (0,0) when the cached panel coordinates were stale.
 */
export class GambleUI extends Container {
  private readonly dim: Graphics;
  private readonly panel: Graphics;
  /** The slot's reel floor under the board, clipped to it; made once the
   *  floor art is there (see {@link setFloor}). */
  private floor: TilingSprite | null = null;
  private readonly floorMask = new Graphics();
  private floorLookup: () => Texture | null = () => null;
  /** Pixi {@link Text} fallback for the title. Producer brief
   *  2026-06-04 — when the commissioned {@code shared:gamble:title}
   *  artwork is loaded, the sprite ({@link titleSprite}) shows instead
   *  and this Text stays hidden. Engine-only test scenes never load
   *  the texture so the Text serves as the readable fallback. */
  private readonly title: Text;
  private titleSprite: Sprite | null = null;
  private readonly subtitle: Text;
  /** Wager amount rendered on its own line below the subtitle. Producer
   *  brief 2026-06-04: split the "Double or nothing — {amount}" line so
   *  the amount gets its own large gold treatment. Hidden during the
   *  card flip + result phases (subtitle carries the live update). */
  private readonly amountText: Text;
  /** Commissioned gold coin icon, painted to the left of the amount
   *  number so the value reads as coins at a glance. Null when the
   *  texture isn't loaded (engine-only test scenes). */
  private coinIcon: Sprite | null = null;
  private redButton: Container;
  private blackButton: Container;
  /** True when a pick button shows the painted card rather than the drawn
   *  fallback. The cards load in the background after the slot opens, so
   *  a gamble offered early used to keep the fallback all visit;
   *  {@link refreshArt} upgrades them on each {@link play}. */
  private redIsArt = false;
  private blackIsArt = false;
  /** Soft glows behind the two pick cards. */
  private readonly glows: Graphics;
  private readonly cardContainer: Container;
  private readonly cardFace: Container;
  /** The commissioned card back, dealt face-down before the reveal. */
  private cardBack: Sprite | null = null;
  /** Gentle float on the pick cards while waiting for a choice. */
  private floatRaf: number | null = null;
  private lastViewport: { w: number; h: number } | null = null;

  private isOpen = false;
  /** Cached panel centre-X so re-runs of {@link positionAmountRow}
   *  after the amount string changes (play start, win) keep the row
   *  centred on the panel. Was previously using {@code amountText.x},
   *  but that's the text's anchor (text-only centre, not the row
   *  centre) — passing it back in shifted the row right by
   *  (coinScaledWidth + gap) / 2 on every call, so a 2-digit amount
   *  like "60" ended up well right of the panel midline. Producer
   *  brief 2026-06-04 "what happened to the coin position? It should
   *  be centred." */
  private panelCenterX = 0;
  private resolveResult: ((result: GambleResult | null) => void) | null = null;
  private currentWinnings = 0;
  /** Who decides this gamble: null means a local draw. */
  private decide: GambleDecide | null = null;
  /** True from a pick until its card is shown, so a second tap does nothing. */
  private picking = false;
  private readonly tweens = new TweenGroup();

  constructor(private readonly random: Random = new DefaultRandom()) {
    super();
    this.alpha = 0;
    this.visible = false;

    this.dim = new Graphics();
    this.dim.eventMode = 'static';
    this.addChild(this.dim);

    this.panel = new Graphics();
    this.addChild(this.floorMask, this.panel);

    this.title = new Text({
      text: 'GAMBLE',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 28,
        fontWeight: '900',
        // Warm gold to match the commissioned button + medallion family
        // (the modal was previously pink-stroked + white-titled which
        // didn't sit with the rest of the casino UI's amber palette).
        fill: 0xffd778,
        letterSpacing: 5,
        dropShadow: {
          color: 0x000000,
          alpha: 0.55,
          distance: 2,
          blur: 0,
          angle: Math.PI / 2,
        },
      },
    });
    this.title.anchor.set(0.5, 0);
    this.addChild(this.title);
    // Commissioned title artwork — replaces the Pixi Text when loaded.
    const titleTexture = AssetLoader.shared.getTexture('shared:gamble:title');
    if (titleTexture) {
      this.titleSprite = new Sprite(titleTexture);
      // Anchor vertically centred so the sprite straddles the panel
      // top edge cleanly when positioned at panelY in {@link layout}.
      // The painted ribbon's brushwork stays sharp because the source
      // PNG is 1536×1024 (wide aspect with the ribbon centred); fit
      // by height preserves proportions.
      this.titleSprite.anchor.set(0.5, 0.5);
      this.titleSprite.scale.set(TITLE_HEIGHT / titleTexture.height);
      this.addChild(this.titleSprite);
      this.title.visible = false;
    }

    this.subtitle = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        // Bumped 15 → 24 + weight 600 → 800 (producer brief 2026-06-04:
        // "the no-match text is lost. Too small and the colour looks
        // washed out"). Bright cream that pops against the dark panel
        // with a hard drop-shadow for separation; was previously
        // 0xf5e6d3 at 0.85 alpha which read as ghosted body text.
        fontSize: 24,
        fontWeight: '800',
        fill: 0xfff4dc,
        align: 'center',
        letterSpacing: 1,
        lineHeight: 28,
        dropShadow: {
          color: 0x000000,
          alpha: 0.6,
          distance: 2,
          blur: 0,
          angle: Math.PI / 2,
        },
      },
    });
    this.subtitle.anchor.set(0.5, 0);
    // alpha kept at 1.0 — previously 0.85 to soften the body but with
    // the bigger font + drop-shadow the text now needs full opacity to
    // hold its own next to the gold amount line.
    this.addChild(this.subtitle);

    this.amountText = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 32,
        fontWeight: '900',
        // Warm gold matching the title + button medallion family.
        fill: 0xffd778,
        align: 'center',
        letterSpacing: 2,
        dropShadow: {
          color: 0x000000,
          alpha: 0.6,
          distance: 2,
          blur: 0,
          angle: Math.PI / 2,
        },
      },
    });
    this.amountText.anchor.set(0.5, 0);
    this.addChild(this.amountText);

    const coinTexture = AssetLoader.shared.getTexture('shared:icon:coin');
    if (coinTexture) {
      this.coinIcon = new Sprite(coinTexture);
      // Anchor centre so the icon vertical-centres against the
      // amount text's cap-height; horizontal position is set in
      // {@link positionAmountRow}.
      this.coinIcon.anchor.set(0.5);
      this.coinIcon.scale.set(COIN_SIZE / coinTexture.height);
      this.addChild(this.coinIcon);
    }

    this.glows = new Graphics();
    this.glows.visible = false;
    this.addChild(this.glows);

    this.redButton = this.buildColorButton('red', 0xd11538, '♥');
    this.redIsArt = AssetLoader.shared.getTexture('card:gamble:red') !== null;
    this.redButton.visible = false;
    this.addChild(this.redButton);

    this.blackButton = this.buildColorButton('black', 0x1a1a2a, '♠');
    this.blackIsArt = AssetLoader.shared.getTexture('card:gamble:black') !== null;
    this.blackButton.visible = false;
    this.addChild(this.blackButton);

    this.cardContainer = new Container();
    this.cardContainer.visible = false;
    this.cardFace = new Container();
    this.cardContainer.addChild(this.cardFace);
    this.addChild(this.cardContainer);
  }

  /** Where the slot's reel floor comes from: the board shows it, faded,
   *  like the boards above the reels. Looked up at each layout, since the
   *  theme's art can load after the scene is built. */
  setFloor(lookup: () => Texture | null): void {
    this.floorLookup = lookup;
  }

  /** Repaint the dim + panel and re-position every child. Safe to call
   * regardless of visible state — invisible elements are positioned too so
   * they're already correct when {@link play} reveals them. */
  layout(viewportWidth: number, viewportHeight: number): void {
    this.lastViewport = { w: viewportWidth, h: viewportHeight };
    this.dim.clear();
    this.dim.rect(0, 0, viewportWidth, viewportHeight).fill({ color: 0x0a0602, alpha: 0.72 });

    const panelWidth = Math.min(PANEL_MAX_WIDTH, Math.floor(viewportWidth * 0.85));
    const panelX = Math.floor((viewportWidth - panelWidth) / 2);
    const panelY = Math.floor((viewportHeight - PANEL_HEIGHT) / 2);
    const panelCenterX = panelX + panelWidth / 2;
    this.panelCenterX = panelCenterX;

    // The board: the slot's own reel floor, faded, with a thin brass edge,
    // as on the boards above the reels (producer 2026-10-14: double or
    // nothing "should be updated to the same background").
    const texture = this.floorLookup();
    if (texture && !this.floor) {
      this.floor = new TilingSprite({ texture, width: 1, height: 1 });
      this.floor.mask = this.floorMask;
      this.addChildAt(this.floor, this.getChildIndex(this.panel));
    }
    this.floorMask.clear().roundRect(panelX, panelY, panelWidth, PANEL_HEIGHT, 16).fill({ color: 0xffffff });
    if (this.floor) {
      this.floor.x = panelX;
      this.floor.y = panelY;
      this.floor.width = panelWidth;
      this.floor.height = PANEL_HEIGHT;
      const scale = 220 / this.floor.texture.width;
      this.floor.tileScale.set(scale, scale);
    }
    this.panel.clear();
    this.panel
      .roundRect(panelX, panelY, panelWidth, PANEL_HEIGHT, 16)
      .fill({ color: 0x180e04, alpha: this.floor ? 0.66 : 1 });
    this.panel
      .roundRect(panelX, panelY, panelWidth, PANEL_HEIGHT, 16)
      .stroke({ color: 0xc8963a, alpha: 1, width: 3 });
    this.panel
      .roundRect(panelX + 4, panelY + 4, panelWidth - 8, PANEL_HEIGHT - 8, 13)
      .stroke({ color: 0xffdc8c, alpha: 0.18, width: 1 });

    this.title.x = panelCenterX;
    this.title.y = panelY + 24;
    if (this.titleSprite) {
      // Vertically centred on the panel TOP edge — half the ribbon
      // sits above the panel, half on it. Intentional chapter-header
      // overlap per producer brief 2026-06-04.
      this.titleSprite.x = panelCenterX;
      this.titleSprite.y = panelY;
    }
    // Subtitle + amount stack below the title artwork. With the bigger
    // title ribbon (now straddling the panel top), the bottom half
    // takes ~TITLE_HEIGHT/2 = 65px into the panel; subtitle starts
    // ~20px under that.
    const titleBottomOnPanel = this.titleSprite
      ? Math.round(TITLE_HEIGHT / 2)
      : 60;
    this.subtitle.x = panelCenterX;
    this.subtitle.y = panelY + titleBottomOnPanel + 20;
    // Amount sits ~24px below the subtitle's baseline (subtitle is now
    // 24pt with anchor (0.5, 0), so subtitle.y + ~26 is its bottom);
    // the previous +46 left the coin/number kissing the subtitle bottom
    // with no breathing space. Producer brief 2026-06-04: "bit of a
    // gap needed between the text and coins."
    this.amountText.y = panelY + titleBottomOnPanel + 70;
    // Coin + amount row — centred horizontally around panelCenterX,
    // coin to the left of the number. {@link positionAmountRow}
    // re-runs whenever the amount text changes (play, win outcome)
    // because text width depends on the rendered digits.
    this.positionAmountRow(panelCenterX);

    // Card buttons centred horizontally. The card scaled width is
    // roughly (CARD_HEIGHT × portrait-ratio) = ~130. Producer 2026-06-10:
    // halve the gap between the two cards — ±110 (220 apart, ~90px gap)
    // read too spread out. ±88 (176 apart) closes the gap to ~46px.
    // Vertical: card half-height + 30px from panel bottom for breathing room.
    const buttonY = panelY + PANEL_HEIGHT - Math.round(CARD_HEIGHT / 2) - 30;
    this.redButton.x = panelCenterX - 88;
    this.redButton.y = buttonY;
    this.blackButton.x = panelCenterX + 88;
    this.blackButton.y = buttonY;

    this.cardContainer.x = panelCenterX;
    this.cardContainer.y = buttonY;

    // Soft gold glows behind each pick card.
    this.glows.clear();
    for (const gx of [panelCenterX - 88, panelCenterX + 88]) {
      for (let i = 5; i >= 1; i--) {
        this.glows
          .ellipse(gx, buttonY + 6, 60 * (i / 5) + 16, 90 * (i / 5) + 16)
          .fill({ color: 0xffc94a, alpha: 0.045 });
      }
    }
  }

  /**
   * Open the gamble UI. Resolves with the result once the player has picked
   * and the card has flipped. Caller updates state from
   * {@link GambleResult.winningsAfter}. With {@code decide}, the colour and
   * the winnings come from it (the server); resolves null when it has no
   * answer, and nothing has changed.
   */
  async play(currentWinnings: number, decide: GambleDecide | null = null): Promise<GambleResult | null> {
    if (this.isOpen) {
      throw new Error('GambleUI is already open');
    }
    this.isOpen = true;
    this.picking = false;
    this.decide = decide;
    this.currentWinnings = currentWinnings;
    // Caps per producer brief 2026-06-04 — matches the all-caps
    // outcome strings (DOUBLED! / NO MATCH) for a consistent
    // capital-letter rhythm through the flow.
    this.subtitle.text = 'DOUBLE OR NOTHING';
    this.amountText.text = currentWinnings.toLocaleString();
    this.amountText.visible = true;
    if (this.coinIcon) this.coinIcon.visible = true;
    // Re-centre the coin+amount pair once the text width has settled
    // with the new digits. MUST use the cached panel centre — passing
    // amountText.x (the text anchor) would shift the row right each
    // call by half the (coin + gap) width.
    this.positionAmountRow(this.panelCenterX);
    this.refreshArt();
    this.resetButtons();
    this.cardContainer.visible = false;
    this.redButton.visible = true;
    this.blackButton.visible = true;
    this.glows.visible = true;
    this.startFloat();

    return new Promise<GambleResult | null>((resolve) => {
      this.resolveResult = resolve;
      this.fadeIn();
    });
  }

  // -------- private --------

  private fadeIn(): void {
    this.visible = true;
    this.scale.set(0.94);
    this.tweens.start({
      durationMs: 220,
      easing: Easings.easeOutBack,
      onUpdate: (p) => {
        this.alpha = p;
        this.scale.set(0.94 + p * 0.06);
      },
    });
  }

  /** Swap in any art that has loaded since this overlay was built (the
   *  cards, coin and title load in the background after the slot opens). */
  private refreshArt(): void {
    let changed = false;
    const swap = (color: GambleColor): void => {
      const old = color === 'red' ? this.redButton : this.blackButton;
      const fresh = this.buildColorButton(color, color === 'red' ? 0xd11538 : 0x1a1a2a, color === 'red' ? '♥' : '♠');
      fresh.visible = old.visible;
      const index = this.getChildIndex(old);
      this.removeChild(old);
      old.destroy({ children: true });
      this.addChildAt(fresh, index);
      if (color === 'red') this.redButton = fresh;
      else this.blackButton = fresh;
      changed = true;
    };
    if (!this.redIsArt && AssetLoader.shared.getTexture('card:gamble:red')) {
      swap('red');
      this.redIsArt = true;
    }
    if (!this.blackIsArt && AssetLoader.shared.getTexture('card:gamble:black')) {
      swap('black');
      this.blackIsArt = true;
    }
    if (!this.titleSprite) {
      const t = AssetLoader.shared.getTexture('shared:gamble:title');
      if (t) {
        this.titleSprite = new Sprite(t);
        this.titleSprite.anchor.set(0.5, 0.5);
        this.titleSprite.scale.set(TITLE_HEIGHT / t.height);
        this.addChild(this.titleSprite);
        this.title.visible = false;
        changed = true;
      }
    }
    if (!this.coinIcon) {
      const t = AssetLoader.shared.getTexture('shared:icon:coin');
      if (t) {
        this.coinIcon = new Sprite(t);
        this.coinIcon.anchor.set(0.5);
        this.coinIcon.scale.set(COIN_SIZE / t.height);
        this.addChild(this.coinIcon);
        changed = true;
      }
    }
    if (changed && this.lastViewport) this.layout(this.lastViewport.w, this.lastViewport.h);
  }

  /** Bob the two pick cards gently, out of step, while the player chooses. */
  private startFloat(): void {
    this.stopFloat();
    const baseRed = this.redButton.y;
    const baseBlack = this.blackButton.y;
    const start = performance.now();
    const step = (now: number): void => {
      const t = (now - start) / 1000;
      this.redButton.y = baseRed + Math.sin(t * 2.2) * 5;
      this.blackButton.y = baseBlack + Math.sin(t * 2.2 + Math.PI) * 5;
      this.floatRaf = requestAnimationFrame(step);
    };
    this.floatRaf = requestAnimationFrame(step);
  }

  private stopFloat(): void {
    if (this.floatRaf !== null) cancelAnimationFrame(this.floatRaf);
    this.floatRaf = null;
    if (this.lastViewport) this.layout(this.lastViewport.w, this.lastViewport.h);
  }

  private resetButtons(): void {
    this.redButton.eventMode = 'static';
    this.redButton.alpha = 1;
    this.blackButton.eventMode = 'static';
    this.blackButton.alpha = 1;
  }

  private buildColorButton(color: GambleColor, bg: number, glyph: string): Container {
    const btn = new Container();
    btn.eventMode = 'static';
    btn.cursor = 'pointer';

    // 2026-06-04 — pick stage now shows the COMMISSIONED card sprite
    // as the button face (instead of the old solid red/black rect +
    // emoji glyph), so the player taps the actual card they're
    // betting on. Falls back to the programmatic Graphics rect when
    // the texture isn't loaded (engine-only test scenes).
    const alias = color === 'red' ? 'card:gamble:red' : 'card:gamble:black';
    const texture = AssetLoader.shared.getTexture(alias);
    if (texture) {
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      // Fit a {@link CARD_HEIGHT}-tall card in the button slot. The
      // commissioned PNG is a square 1024×1024 with the painted card
      // body centred in portrait proportions, so a uniform fit-by-
      // height keeps the body inside the slot without stretching.
      sprite.scale.set(CARD_HEIGHT / texture.height);
      btn.addChild(sprite);
      // No grey/gold highlight ring — producer brief 2026-06-04:
      // the painterly card already has its own outline + inner shadow,
      // wrapping it in a UI ring flattened the visual.
    } else {
      const g = new Graphics();
      g.roundRect(-56, -40, 112, 80, 14)
        .fill({ color: bg })
        .stroke({ color: 0xffffff, alpha: 0.55, width: 2 });
      btn.addChild(g);
    }

    // Producer brief 2026-06-04 — sprite mode skips the RED/BLACK
    // label text entirely; the painterly card face carries the
    // identity on its own. Programmatic fallback (engine-only test
    // scenes) keeps the label since the colour rect needs the cue.
    if (!texture) {
      const t = new Text({
        text: `${glyph}\n${color.toUpperCase()}`,
        style: {
          fontFamily: FONT_FAMILY,
          fontSize: 16,
          fontWeight: '900',
          fill: 0xffffff,
          align: 'center',
          letterSpacing: 2,
          lineHeight: 22,
        },
      });
      t.anchor.set(0.5);
      btn.addChild(t);
    }

    btn.on('pointertap', () => this.handlePick(color));
    return btn;
  }

  private handlePick(picked: GambleColor): void {
    if (!this.resolveResult || this.picking) return;
    this.picking = true;
    void this.pick(picked);
  }

  private async pick(picked: GambleColor): Promise<void> {
    this.stopFloat();
    this.glows.visible = false;

    // Disable buttons; the one picked stays a little brighter.
    this.redButton.eventMode = 'auto';
    this.redButton.alpha = picked === 'red' ? 0.35 : 0.12;
    this.blackButton.eventMode = 'auto';
    this.blackButton.alpha = picked === 'black' ? 0.35 : 0.12;

    this.subtitle.text = '…MATCH?';
    // Amount line steps aside while the subtitle carries the live
    // status text through the flip + result phases.
    this.amountText.visible = false;
    if (this.coinIcon) this.coinIcon.visible = false;

    let actualColor: GambleColor;
    let decidedWinnings: number | null = null;
    if (this.decide) {
      try {
        const answer = await this.decide(picked);
        actualColor = answer.actualColor;
        decidedWinnings = answer.winningsAfter;
      } catch (err) {
        console.warn('[gamble] no answer from the server; nothing changed', err);
        this.subtitle.text = 'TRY AGAIN LATER';
        setTimeout(() => this.fadeOutAndResolve(null), 900);
        return;
      }
    } else {
      actualColor = this.random.nextBoolean() ? 'red' : 'black';
    }

    // 2026-06-04 — drop the horizontal-squish flip animation entirely.
    // The commissioned cards are painted at a slight tilt, so the
    // scale.x 1 → 0 → 1 squash read as the card warping at the wrong
    // axis rather than flipping. Now: paint the result face directly,
    // brief pop-in (alpha + uniform scale) so the moment of reveal
    // still has weight without the off-axis distortion.
    // 2026-10-06: deal the commissioned card BACK first, hold it a beat
    // for suspense, then cross-fade to the drawn face with a pop. Still no
    // horizontal squash (see above). Falls back to the face pop-in when
    // the card-back art isn't loaded.
    this.paintCardFace(actualColor);
    const back = this.paintCardBack();
    this.cardContainer.visible = true;
    this.cardContainer.scale.set(1);
    this.cardContainer.alpha = back ? 1 : 0;
    this.cardFace.alpha = back ? 0 : 1;
    this.tweens.start({
      durationMs: back ? 900 : 280,
      easing: back ? Easings.linear : Easings.easeOutBack,
      onUpdate: (p) => {
        if (!back) {
          this.cardContainer.alpha = p;
          // Uniform scale 0.7 → 1.0 for a subtle pop without horizontal
          // squish; eases through 1.05 thanks to easeOutBack.
          this.cardContainer.scale.set(0.7 + p * 0.3);
          return;
        }
        if (p < 0.3) {
          // Deal: the back slides up into place.
          const q = Easings.easeOutCubic(p / 0.3);
          back.alpha = q;
          back.y = (1 - q) * 40;
          this.cardContainer.scale.set(0.8 + q * 0.2);
        } else if (p < 0.6) {
          // Suspense: a slight shiver.
          back.alpha = 1;
          back.y = 0;
          back.rotation = Math.sin((p - 0.3) * 60) * 0.025;
        } else {
          // Reveal: back fades as the face pops in.
          const q = (p - 0.6) / 0.4;
          back.rotation = 0;
          back.alpha = 1 - q;
          this.cardFace.alpha = q;
          this.cardContainer.scale.set(1 + Math.sin(q * Math.PI) * 0.1);
        }
      },
      onComplete: () => {
        this.cardContainer.alpha = 1;
        this.cardContainer.scale.set(1);
        this.cardFace.alpha = 1;
        if (back) back.visible = false;
        const outcome: 'win' | 'lose' = picked === actualColor ? 'win' : 'lose';
        const winningsAfter = decidedWinnings ?? (outcome === 'win' ? this.currentWinnings * 2 : 0);
        if (outcome === 'win') {
          // Producer brief 2026-06-04 — WIN gets the full celebration:
          // big "DOUBLED!" subtitle, the doubled amount restored in the
          // gold amount line, a confetti burst from the card position,
          // and a big win sting. Was previously just "WIN → 200" in
          // small subtitle text.
          this.subtitle.text = 'DOUBLED!';
          this.amountText.text = winningsAfter.toLocaleString();
          this.amountText.scale.set(1.35);
          this.amountText.visible = true;
          if (this.coinIcon) {
            this.coinIcon.visible = true;
            this.coinIcon.scale.set((COIN_SIZE * 1.35) / (this.coinIcon.texture.height));
          }
          // Re-centre the row now that text width changed (digits
          // doubled). Use the cached panel centre — see panelCenterX
          // field comment for why amountText.x is the wrong anchor.
          this.positionAmountRow(this.panelCenterX);
          this.spawnConfetti();
          AudioBus.shared.play('win:sting_big', { volume: 0.85 });
        } else {
          this.subtitle.text = 'NO MATCH';
          // amountText stays hidden (lose = nothing to celebrate).
        }
        // Brief hold so the player can read the result. Win gets a
        // longer beat so the confetti + sting have time to land.
        const holdMs = outcome === 'win' ? 1400 : 700;
        setTimeout(() => {
          const result: GambleResult = { outcome, picked, actualColor, winningsAfter };
          // Reset the scale so the next play() starts clean.
          this.amountText.scale.set(1);
          if (this.coinIcon) {
            this.coinIcon.scale.set(COIN_SIZE / this.coinIcon.texture.height);
          }
          this.fadeOutAndResolve(result);
        }, holdMs);
      },
    });
  }

  // paintCardBack was used during the horizontal-squish flip — now
  // removed in favour of a direct reveal (producer brief 2026-06-04
  // "don't show the card rotation on gamble, just reveal card"). Kept
  // here as a comment so the back-card recipe can be restored quickly
  // if a future revision wants a different flip / shuffle animation.

  /** The face-down card dealt before the reveal, or null when the
   *  card-back art isn't loaded. Sits above the face in the container. */
  private paintCardBack(): Sprite | null {
    const texture = AssetLoader.shared.getTexture('card:back');
    if (!texture) return null;
    if (!this.cardBack) {
      this.cardBack = new Sprite(texture);
      this.cardBack.anchor.set(0.5);
      this.cardContainer.addChild(this.cardBack);
    }
    this.cardBack.scale.set(REVEAL_CARD_HEIGHT / texture.height);
    this.cardBack.visible = true;
    this.cardBack.alpha = 0;
    this.cardBack.rotation = 0;
    return this.cardBack;
  }

  private paintCardFace(actualColor: GambleColor): void {
    this.cardFace.removeChildren().forEach((child) => child.destroy());
    // 2026-06-04 — commissioned painterly card face replaces the
    // programmatic white-rect + ♥/♠ glyph. Sprite mode kicks in when
    // the gamble card texture is loaded (it ships in
    // {@code SHARED_GAME_TEXTURES} so every commissioned theme picks
    // it up at AssetLoader.loadTheme time). When the texture is
    // missing — engine-only test scenes — the original Graphics +
    // glyph path runs as a fallback.
    const alias = actualColor === 'red' ? 'card:gamble:red' : 'card:gamble:black';
    const texture = AssetLoader.shared.getTexture(alias);
    if (texture) {
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      // Bigger than the pick-stage cards so the reveal has weight.
      // The pick buttons are faded to 0.4 by handlePick before this
      // runs, so the larger reveal sits on top as the focal point.
      sprite.scale.set(REVEAL_CARD_HEIGHT / texture.height);
      this.cardFace.addChild(sprite);
      return;
    }
    // Programmatic fallback (no commissioned texture loaded).
    const bg = new Graphics();
    bg
      .roundRect(-50, -68, 100, 136, 12)
      .fill({ color: 0xffffff })
      .stroke({ color: 0x1a0830, width: 2 });
    this.cardFace.addChild(bg);
    const symbol = new Text({
      text: actualColor === 'red' ? '♥' : '♠',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 56,
        fontWeight: '900',
        fill: actualColor === 'red' ? 0xd11538 : 0x1a1a2a,
      },
    });
    symbol.anchor.set(0.5);
    this.cardFace.addChild(symbol);
  }

  /** Horizontal-centre the coin + amount pair around {@code centerX}.
   *  The coin sits to the left of the number with ~8px breathing
   *  space; both share the same Y as {@link amountText}'s anchor row.
   *  Re-runs whenever the amount text changes (play start, win
   *  outcome) because the text's width depends on the digits. */
  private positionAmountRow(centerX: number): void {
    const textWidth = this.amountText.width;
    const coinScaledWidth = this.coinIcon
      ? COIN_SIZE * this.coinIcon.scale.x * (this.coinIcon.texture.width / this.coinIcon.texture.height)
      : 0;
    // 18px breathing room — earlier 10 had the coin's right edge
    // touching the "1" of three-digit amounts. Producer brief
    // 2026-06-04 "need some space between."
    const gap = this.coinIcon ? 18 : 0;
    const totalWidth = coinScaledWidth + gap + textWidth;
    if (this.coinIcon) {
      this.coinIcon.x = centerX - totalWidth / 2 + coinScaledWidth / 2;
      // Vertical-centre coin against the text's cap height (~70% of
      // the line height for amountText's 32pt font; the scaled amount
      // sits at scale.y too).
      this.coinIcon.y = this.amountText.y + (this.amountText.height * 0.5);
    }
    this.amountText.x = centerX - totalWidth / 2 + coinScaledWidth + gap + textWidth / 2;
  }

  /** Confetti burst at the card position — 30 small painted squares
   *  fan upward with gravity, fade over 1.4s. Producer brief
   *  2026-06-04 — the previous WIN result was just a single line of
   *  small subtitle text; the burst gives the moment proper weight. */
  private spawnConfetti(): void {
    const colors = [
      0xffd778, // gold (matches title + amount)
      0xff6bd6, // pink (brand)
      0xff8a3d, // orange
      0xffffff, // white
      0x4af0ff, // cyan
      0xb0ff5f, // lime
    ];
    const PARTICLE_COUNT = 36;
    // Burst origin = card centre (matches cardContainer position
    // computed in layout()). Falling back to (0,0) is fine — the
    // particles use their own absolute positions via the tween.
    const originX = this.cardContainer.x;
    const originY = this.cardContainer.y;
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const piece = new Graphics();
      const color = colors[Math.floor(Math.random() * colors.length)] ?? 0xffd778;
      const size = 5 + Math.random() * 5; // 5–10 px square
      piece.rect(-size / 2, -size / 2, size, size).fill({ color });
      piece.x = originX;
      piece.y = originY;
      piece.rotation = Math.random() * Math.PI * 2;
      this.addChild(piece);

      // Random launch — upward-biased fan ±60° around straight up,
      // initial speed 240–440 px/s. Gravity 700 px/s² pulls down.
      const angle = (Math.random() - 0.5) * Math.PI * (2 / 3); // ~±60°
      const speed = 240 + Math.random() * 200;
      const vx = Math.sin(angle) * speed;
      const vy = -Math.cos(angle) * speed; // negative = up
      const rotSpeed = (Math.random() - 0.5) * 12; // rad/s
      const lifetimeS = 1.4;

      this.tweens.start({
        durationMs: lifetimeS * 1000,
        easing: Easings.linear,
        onUpdate: (p) => {
          const t = p * lifetimeS;
          piece.x = originX + vx * t;
          piece.y = originY + vy * t + 0.5 * 700 * t * t;
          piece.rotation += rotSpeed * (1 / 60);
          // Fade out across the final 40% of the lifetime.
          piece.alpha = p < 0.6 ? 1 : 1 - (p - 0.6) / 0.4;
        },
        onComplete: () => {
          piece.destroy();
        },
      });
    }
  }

  private fadeOutAndResolve(result: GambleResult | null): void {
    const resolver = this.resolveResult;
    this.resolveResult = null;
    this.tweens.start({
      durationMs: 200,
      easing: Easings.easeOutCubic,
      onUpdate: (p) => {
        this.alpha = 1 - p;
      },
      onComplete: () => {
        this.visible = false;
        this.isOpen = false;
        this.redButton.visible = false;
        this.blackButton.visible = false;
        this.cardContainer.visible = false;
        if (resolver) resolver(result);
      },
    });
  }

  /** Cancel any in-flight gamble animations + reject the open promise.
   * Called from host components on destroy. */
  destroyGamble(): void {
    this.tweens.cancelAll();
    if (this.floatRaf !== null) cancelAnimationFrame(this.floatRaf);
    this.floatRaf = null;
    if (this.resolveResult) {
      // Resolve with "no answer" so awaiting code doesn't hang forever;
      // scenes typically destroy because the user navigated away.
      this.resolveResult(null);
      this.resolveResult = null;
    }
  }
}
